<template>
  <div v-if="isEditing" class="flex min-w-0 items-center gap-1 overflow-hidden">
    <input
      ref="inputRef"
      v-model="draft"
      type="text"
      class="w-full min-w-0 truncate whitespace-nowrap rounded-sm bg-accent/12 text-sm tracking-[0.02em] text-text-muted outline-none"
      :title="value"
      spellcheck="false"
      data-no-drag
      data-field="name"
      @blur="void commitEdit()"
      @keydown="onEditKeydown"
    />
    <EmojiPickerButton :label="messages.player.addEmoji" @select="appendEmoji" />
  </div>
  <button
    v-else-if="canEdit"
    type="button"
    data-no-drag
    class="block w-full min-w-0 truncate whitespace-nowrap rounded-sm text-left text-sm tracking-[0.02em] text-text-muted outline-none transition-[background-color] duration-120 ease-standard focus:bg-accent/12 cursor-text"
    :title="value"
    data-field="name"
    @click="startEdit"
  >{{ value }}</button>
  <span
    v-else
    class="block w-full min-w-0 truncate whitespace-nowrap text-sm tracking-[0.02em] text-text-muted"
    :title="value"
  >{{ value }}</span>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, type Ref } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useInlineEditableTextField } from '@/shared/composables'
import { EmojiPickerButton } from '@/shared/ui'

import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'

export interface PlayerNameFieldProps {
  /** The persisted in-game name displayed below the live guild nickname. */
  readonly value: string
  /**
   * Permission gate. When `false`, the click-to-edit affordance is
   * suppressed and the field renders as a plain read-only `<span>`.
   * Mirrors `PlayerCard.canEdit` (officer + Live board mode).
   */
  readonly canEdit: boolean
}

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const props = defineProps<PlayerNameFieldProps>()

const emit = defineEmits<{
  /** Fired on commit (Enter / blur) with the trimmed in-game name. */
  (e: 'commit', value: string): void
}>()

const valueRef: ComputedRef<string> = computed(() => props.value)

const canEditRef: ComputedRef<boolean> = computed(() => props.canEdit)

const editable = useInlineEditableTextField<'name'>({
  currentValue: valueRef,
  field: 'name',
  canEdit: canEditRef,
  onCommit: (next: string) => emit('commit', next),
})
const draft = editable.draft
const inputRef = editable.inputRef
const isEditing = editable.isEditing
const startEdit = editable.startEdit
const commitEdit = editable.commitEdit
const cancelEdit = editable.cancelEdit
// `inputRef` is bound to the `<input ref="inputRef">` template — the
// composable's watcher focuses + selects it on edit-mode entry. Pin
// the local ref here so TS's `noUnusedLocals` sees the binding rather
// than assuming the destructured property is dead.
void inputRef

function onEditKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    event.preventDefault()
    const target = event.target
    if (target instanceof HTMLInputElement) target.blur()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    cancelEdit()
  }
}

function appendEmoji(emoji: string): void {
  draft.value = `${draft.value}${emoji}`
  inputRef.value?.focus()
}

// Re-exported so parents can treat this as a self-contained inline-edit field.
export type PlayerNameFieldInputRef = Ref<HTMLInputElement | null>
</script>
